import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  FolderOpen,
  Globe,
  HeartPulse,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  Moon,
  Package,
  Settings,
  Stethoscope,
  Sun,
  SunMoon,
  User,
  Users,
  Utensils,
  Warehouse,
  Wallet,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useLang } from '@/providers/LangProvider';
import { useTheme, type Theme } from '@/providers/ThemeProvider';
import { useToast } from '@/providers/ToastProvider';
import { clinicName } from '@/lib/supabase';
import { staffRoleLabelKey } from '@/lib/staffRoles';
import { t } from '@/i18n/labels';
import {
  canViewBilling,
  canViewClinical,
  canViewEmployees,
  canViewFiles,
  canViewFinance,
  canViewInventory,
  canViewNutrition,
  canViewPatients,
  canViewReports,
  canViewSchedule,
  canViewServices,
} from '@/lib/permissions';
import type { StaffRole } from '@/types';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/IconButton';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';
import { errorMessage } from '@/lib/errorMessage';
import { InstallPrompt } from '@/layouts/InstallPrompt';

type NavItem = { to: string; labelKey: string; icon: LucideIcon; canView?: (role: StaffRole | undefined) => boolean };
type NavSection = { titleKey: string; items: NavItem[] };

const NAV_SECTIONS: NavSection[] = [
  {
    titleKey: 'navSectionMain',
    items: [
      { to: '/',         labelKey: 'dashboard',  icon: LayoutDashboard },
      { to: '/patients', labelKey: 'patients',   icon: Users,       canView: canViewPatients },
      { to: '/schedule', labelKey: 'schedule',   icon: CalendarDays, canView: canViewSchedule },
    ],
  },
  {
    titleKey: 'navSectionClinical',
    items: [
      { to: '/assessments', labelKey: 'assessments', icon: ClipboardList, canView: canViewClinical },
      { to: '/plans',       labelKey: 'plans',       icon: HeartPulse,    canView: canViewClinical },
      { to: '/sessions',    labelKey: 'sessions',    icon: Activity,      canView: canViewClinical },
      { to: '/nutrition',   labelKey: 'nutrition',   icon: Utensils,      canView: canViewNutrition },
    ],
  },
  {
    titleKey: 'navSectionOperations',
    items: [
      { to: '/services',  labelKey: 'services',  icon: Stethoscope, canView: canViewServices },
      { to: '/packages',  labelKey: 'packages',  icon: Package,     canView: canViewServices },
      { to: '/inventory', labelKey: 'inventory', icon: Warehouse,   canView: canViewInventory },
      { to: '/files',     labelKey: 'files',     icon: FolderOpen,  canView: canViewFiles },
    ],
  },
  {
    titleKey: 'navSectionFinance',
    items: [
      { to: '/billing', labelKey: 'billing', icon: WalletCards, canView: canViewBilling },
      { to: '/finance', labelKey: 'finance', icon: Wallet,      canView: canViewFinance },
      { to: '/reports', labelKey: 'reports', icon: FileText,    canView: canViewReports },
    ],
  },
  {
    titleKey: 'navSectionAdmin',
    items: [
      { to: '/employees', labelKey: 'employees', icon: Users,     canView: canViewEmployees },
      { to: '/settings',  labelKey: 'settings',  icon: Settings },
    ],
  },
];

const MOBILE_PRIMARY: NavItem[] = [
  { to: '/',         labelKey: 'dashboard', icon: LayoutDashboard },
  { to: '/patients', labelKey: 'patients',  icon: Users,      canView: canViewPatients },
  { to: '/schedule', labelKey: 'schedule',  icon: CalendarDays, canView: canViewSchedule },
  { to: '/more',     labelKey: 'more',      icon: LayoutGrid },
  { to: '/settings', labelKey: 'settings',  icon: Settings },
];

const COLLAPSE_KEY = 'wiqaya-sidebar-collapsed-v1';

export function AppShell() {
  const { profile, signOut } = useAuth();
  const { lang, setLang, dir } = useLang();
  const { theme, setTheme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close menus on route change
  useEffect(() => {
    setUserMenuOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    function onDown(e: MouseEvent) {
      const tgt = e.target as HTMLElement | null;
      if (tgt?.closest('.user-menu')) return;
      setUserMenuOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  const onSignOut = useCallback(async () => {
    try {
      await signOut();
      toast.info(t(lang, 'signOut'));
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(t(lang, 'signOut'), errorMessage(err));
    }
  }, [signOut, navigate, toast, lang]);

  const cycleTheme = useCallback(() => {
    const order: Theme[] = ['light', 'dark', 'auto'];
    const next = order[(order.indexOf(theme) + 1) % order.length]!;
    setTheme(next);
    toast.info(t(lang, 'theme'), t(lang, themeLabelKey(next)));
  }, [setTheme, theme, toast, lang]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return t(lang, 'greetingNight');
    if (h < 12) return t(lang, 'greetingMorning');
    if (h < 17) return t(lang, 'greetingAfternoon');
    if (h < 21) return t(lang, 'greetingEvening');
    return t(lang, 'greetingNight');
  }, [lang]);

  const pageTitle = useMemo(() => pageTitleFor(location.pathname), [location.pathname]);

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : SunMoon;

  const role = profile?.role;

  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.canView || item.canView(role)),
      })).filter((section) => section.items.length > 0),
    [role],
  );

  const visibleMobilePrimary = useMemo(
    () => MOBILE_PRIMARY.filter((item) => !item.canView || item.canView(role)),
    [role],
  );

  return (
    <div className={`shell${collapsed && !isMobile ? ' collapsed' : ''}`} dir={dir}>
      {/* Desktop sidebar */}
      <aside className="shell-sidebar" aria-label={t(lang, 'navigation')}>
        <a href="/" className="shell-brand" aria-label={clinicName}>
          <span className="brand-mark small" aria-hidden />
          <span className="shell-brand-text">
            <strong>{clinicName}</strong>
            <span className="shell-brand-sub">{t(lang, 'appTitle')}</span>
          </span>
          <button
            type="button"
            className="shell-collapse-btn"
            aria-label={collapsed ? t(lang, 'expandSidebar') : t(lang, 'collapseSidebar')}
            onClick={(e) => {
              e.preventDefault();
              setCollapsed((v) => !v);
            }}
          >
            {dir === 'rtl' ? (collapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />) : (collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />)}
          </button>
        </a>

        <nav className="shell-nav" aria-label={t(lang, 'navigation')}>
          {visibleSections.map((section) => (
            <div key={section.titleKey} className="shell-nav-section">
              <p className="shell-nav-section-title">{t(lang, section.titleKey)}</p>
              {section.items.map(({ to, labelKey, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) => `shell-nav-link${isActive ? ' active' : ''}`}
                  title={collapsed ? t(lang, labelKey) : undefined}
                >
                  <span className="shell-nav-icon" aria-hidden>
                    <Icon size={18} />
                  </span>
                  <span className="shell-nav-label">{t(lang, labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="shell-footer">
          <div className="shell-footer-meta">
            <Avatar name={profile?.full_name ?? '?'} size="sm" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="shell-footer-meta-name">{profile?.full_name ?? '—'}</div>
              <div className="shell-footer-meta-role">{profile?.role ? t(lang, staffRoleLabelKey(profile.role)) : ''}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && isMobile ? (
        <>
          <div className="mobile-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <aside className="mobile-drawer" aria-label={t(lang, 'navigation')}>
            <div className="shell-brand" style={{ padding: '0.4rem 0.4rem 1rem' }}>
              <span className="brand-mark small" aria-hidden />
              <span className="shell-brand-text">
                <strong>{clinicName}</strong>
                <span className="shell-brand-sub">{t(lang, 'appTitle')}</span>
              </span>
            </div>
            <nav className="shell-nav" aria-label={t(lang, 'navigation')}>
              {visibleSections.map((section) => (
                <div key={section.titleKey} className="shell-nav-section">
                  <p className="shell-nav-section-title">{t(lang, section.titleKey)}</p>
                  {section.items.map(({ to, labelKey, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === '/'}
                      className={({ isActive }) => `shell-nav-link${isActive ? ' active' : ''}`}
                      onClick={() => setDrawerOpen(false)}
                    >
                      <span className="shell-nav-icon" aria-hidden>
                        <Icon size={18} />
                      </span>
                      <span className="shell-nav-label">{t(lang, labelKey)}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          </aside>
        </>
      ) : null}

      {/* Main column */}
      <div className="shell-main">
        <header className="shell-topbar">
          <div className="shell-topbar-titles" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
            {isMobile ? (
              <IconButton
                aria-label={t(lang, 'openMenu')}
                onClick={() => setDrawerOpen(true)}
                size="sm"
                variant="ghost"
              >
                <Menu size={18} />
              </IconButton>
            ) : null}
            <div style={{ minWidth: 0 }}>
              <p className="shell-greeting">
                {greeting}, {profile?.full_name?.split(' ')[0] ?? '—'}
              </p>
              <p className="shell-role">{pageTitle ? t(lang, pageTitle) : t(lang, 'welcome')}</p>
            </div>
          </div>

          <div className="shell-topbar-actions">
            <IconButton
              aria-label={t(lang, 'language')}
              onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
              size="sm"
              variant="ghost"
            >
              <Globe size={16} />
            </IconButton>
            <IconButton
              aria-label={t(lang, 'theme')}
              onClick={cycleTheme}
              size="sm"
              variant="ghost"
              title={t(lang, themeLabelKey(theme))}
            >
              <ThemeIcon size={16} />
            </IconButton>
            <IconButton
              aria-label={t(lang, 'notifications')}
              size="sm"
              variant="ghost"
              onClick={() => toast.info(t(lang, 'notifications'), t(lang, 'noNotifications'))}
            >
              <Bell size={16} />
            </IconButton>
            <div className="user-menu">
              <button
                type="button"
                className="user-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                onClick={() => setUserMenuOpen((v) => !v)}
              >
                <Avatar name={profile?.full_name ?? '?'} size="sm" />
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }} className="user-menu-name">
                  {profile?.full_name?.split(' ')[0] ?? '—'}
                </span>
              </button>
              {userMenuOpen ? (
                <div className="user-menu-pop" role="menu">
                  <div style={{ padding: '0.45rem 0.65rem 0.2rem' }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{profile?.full_name ?? '—'}</div>
                    <div className="muted" style={{ fontSize: '0.78rem' }}>
                      {profile?.role ? t(lang, staffRoleLabelKey(profile.role)) : ''}
                    </div>
                  </div>
                  <div className="user-menu-divider" />
                  <button type="button" role="menuitem" className="user-menu-item" onClick={() => navigate('/settings')}>
                    <User size={16} />
                    {t(lang, 'settings')}
                  </button>
                  <button type="button" role="menuitem" className="user-menu-item" onClick={cycleTheme}>
                    <ThemeIcon size={16} />
                    {t(lang, 'theme')} · {t(lang, themeLabelKey(theme))}
                  </button>
                  <div className="user-menu-divider" />
                  <button type="button" role="menuitem" className="user-menu-item danger" onClick={() => void onSignOut()}>
                    <LogOut size={16} />
                    {t(lang, 'signOut')}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="shell-content fade-in">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="shell-mobile-nav" aria-label={t(lang, 'navigation')}>
        {visibleMobilePrimary.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `shell-mobile-link${isActive ? ' active' : ''}`}
          >
            <Icon size={20} aria-hidden />
            <span>{t(lang, labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      <InstallPrompt />
    </div>
  );
}

function themeLabelKey(theme: Theme): string {
  if (theme === 'dark') return 'themeDark';
  if (theme === 'light') return 'themeLight';
  return 'themeAuto';
}

function pageTitleFor(pathname: string): string | null {
  const seg = pathname.split('/').filter(Boolean)[0];
  switch (seg) {
    case undefined: return 'dashboard';
    case 'patients': return 'patients';
    case 'schedule': return 'schedule';
    case 'services': return 'services';
    case 'packages': return 'packages';
    case 'inventory': return 'inventory';
    case 'assessments': return 'assessments';
    case 'plans': return 'plans';
    case 'sessions': return 'sessions';
    case 'nutrition': return 'nutrition';
    case 'billing': return 'billing';
    case 'finance': return 'finance';
    case 'reports': return 'reports';
    case 'files': return 'files';
    case 'employees': return 'employees';
    case 'settings': return 'settings';
    case 'more': return 'more';
    default: return null;
  }
}
